import React, { useState } from "react";
import { CalendarClock, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";

export default function SubscriptionCard({ onViewPlans }) {
  const { t, lang } = useLang();
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const sub = user?.subscription;

  const cancel = async () => {
    if (!window.confirm(t("sub.confirmCancel"))) return;
    setBusy(true);
    try {
      await api.post("/subscription/cancel");
      toast.success(t("sub.cancelled"));
      await refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || t("sub.cancelErr")); }
    finally { setBusy(false); }
  };

  if (!sub) {
    return (
      <div className="rounded-xl border border-[#243047] bg-[#121824] p-5" data-testid="subscription-card">
        <h3 className="font-heading font-bold flex items-center gap-2 mb-2"><CalendarClock className="w-4 h-4 text-[#EC1E9C]" /> {t("sub.title")}</h3>
        <p className="text-sm text-[#94A3B8]">{t("sub.cta")}</p>
        <button onClick={onViewPlans} data-testid="subscription-view-plans"
          className="mt-3 w-full flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#EC1E9C] to-[#00FFC2] text-[#03110B] font-bold py-2 text-sm hover:brightness-110 transition">
          <Sparkles className="w-4 h-4" /> {t("sub.view")}
        </button>
      </div>
    );
  }

  const end = sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB") : "-";
  return (
    <div className="rounded-xl border border-[#EC1E9C]/50 bg-[#121824] p-5" data-testid="subscription-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading font-bold flex items-center gap-2"><CalendarClock className="w-4 h-4 text-[#EC1E9C]" /> {t("sub.title")}</h3>
        <span className="text-[10px] font-bold uppercase bg-gradient-to-r from-[#EC1E9C] to-[#00FFC2] text-[#03110B] px-2 py-0.5 rounded-full" data-testid="subscription-plan-name">{sub.plan_name}</span>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-[#EC1E9C] to-[#00FFC2]" data-testid="subscription-today-credits">{user.sub_credits}</span>
        <span className="text-xs text-[#94A3B8]">{t("sub.today")} · {t("sub.ofDaily", { n: sub.daily_signals })}</span>
      </div>
      <p className="text-[11px] text-[#64748B] mt-2 leading-relaxed">{t("sub.resetNote")}</p>
      <div className="mt-3 text-xs font-mono text-[#94A3B8]" data-testid="subscription-period-end">
        {sub.cancel_at_period_end ? t("sub.endsOn") : t("sub.renews")} <span className="text-white">{end}</span>
      </div>
      {!sub.cancel_at_period_end && (
        <button onClick={cancel} disabled={busy} data-testid="subscription-cancel-button"
          className="mt-3 text-xs font-semibold text-[#94A3B8] hover:text-[#EC1E9C] transition disabled:opacity-50 flex items-center gap-1.5">
          {busy && <Loader2 className="w-3 h-3 animate-spin" />} {busy ? t("sub.cancelling") : t("sub.cancel")}
        </button>
      )}
    </div>
  );
}
